import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  apiCreateTransfer,
  apiDeleteTransfer,
  apiListAccounts,
  apiListTransfers,
  type AccountRow,
  type TransferDirection,
  type TransferRow,
} from "../api";
import { currentMonthISO, formatCents, monthRange } from "../utils/money";
import { accountTypeLabel } from "../accountTypes";

export function TransfersPage() {
  const [month, setMonth] = useState(currentMonthISO);
  const [accountFilter, setAccountFilter] = useState("");
  const [direction, setDirection] = useState<TransferDirection>("either");
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [transfers, setTransfers] = useState<TransferRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredOn, setOccurredOn] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState("");

  const loadAll = useCallback(async () => {
    const { from, to } = monthRange(month);
    const [a, tr] = await Promise.all([
      apiListAccounts(),
      apiListTransfers({
        from,
        to,
        accountId: accountFilter || undefined,
        direction: accountFilter ? direction : undefined,
      }),
    ]);
    setAccounts(a.accounts);
    setTransfers(tr.transfers);
  }, [month, accountFilter, direction]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadAll();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  useEffect(() => {
    if (accounts.length === 0) return;
    const cash = accounts.find((x) => x.type === "cash");
    setFromId((prev) => {
      if (prev && accounts.some((a) => a.id === prev)) return prev;
      return (cash ?? accounts[0]).id;
    });
    setToId((prev) => {
      if (prev && accounts.some((a) => a.id === prev)) return prev;
      const preferredFrom = cash?.id ?? accounts[0]?.id;
      const other = accounts.find((a) => a.id !== preferredFrom);
      return other?.id ?? prev;
    });
  }, [accounts]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const amt = Number(amount);
    if (!fromId || !toId || fromId === toId) {
      setError("Choose two different accounts.");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Enter a positive amount.");
      return;
    }
    setBusy(true);
    try {
      await apiCreateTransfer({
        from_account_id: fromId,
        to_account_id: toId,
        amount: amt,
        occurred_on: occurredOn,
        notes: notes.trim() || null,
      });
      setAmount("");
      setNotes("");
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this transfer?")) return;
    setError(null);
    try {
      await apiDeleteTransfer(id);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
  }

  if (loading && accounts.length === 0) {
    return <p className="muted">Loading…</p>;
  }

  return (
    <div className="stack">
      <div className="card row-between">
        <div>
          <h2 className="mt-0">Transfers</h2>
          <p className="muted mb-0">
            Move money between your accounts (for example into a savings goal). Transfers do not count as income or
            expenses in the monthly summary. To log pay or spending, use{" "}
            <Link to="/transactions">Transactions</Link> and pick the account there.
          </p>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="card">
        <h3 className="mt-0">New transfer</h3>
        {accounts.length < 2 ? (
          <p className="muted mb-0">
            Add at least two accounts on <Link to="/accounts">Accounts</Link> (for example <strong>Cash</strong> and a{" "}
            <strong>Savings</strong> goal account) before transferring.
          </p>
        ) : (
          <form className="stack" onSubmit={(e) => void onSubmit(e)} style={{ maxWidth: "28rem" }}>
            <div className="form-row">
              <label>
                From
                <select value={fromId} onChange={(e) => setFromId(e.currentTarget.value)}>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({accountTypeLabel(a.type)})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                To
                <select value={toId} onChange={(e) => setToId(e.currentTarget.value)}>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id} disabled={a.id === fromId}>
                      {a.name} ({accountTypeLabel(a.type)})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Amount
                <input
                  type="number"
                  inputMode="decimal"
                  min={0.01}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.currentTarget.value)}
                  placeholder="0.00"
                  required
                />
              </label>
              <label>
                Date
                <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.currentTarget.value)} required />
              </label>
            </div>
            <label>
              Notes (optional)
              <input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="e.g. April goal" />
            </label>
            <button type="submit" disabled={busy}>
              Record transfer
            </button>
          </form>
        )}
      </div>

      <div className="card">
        <h3 className="mt-0">Transfers in selected month</h3>
        <div className="form-row filters">
          <label>
            Month
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.currentTarget.value)}
              className="input-compact"
            />
          </label>
          <label>
            Account
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.currentTarget.value)}
            >
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          {accountFilter ? (
            <label>
              Direction
              <select
                value={direction}
                onChange={(e) => setDirection(e.currentTarget.value as TransferDirection)}
              >
                <option value="either">Either direction</option>
                <option value="from">From this account (money out)</option>
                <option value="to">To this account (money in)</option>
              </select>
            </label>
          ) : null}
        </div>
        {transfers.length === 0 ? (
          <p className="muted mb-0">No transfers match these filters.</p>
        ) : (
          <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>From → To</th>
                <th>Notes</th>
                <th className="right">Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id}>
                  <td className="mono">{t.occurred_on}</td>
                  <td>
                    {t.from_account_name} → {t.to_account_name}
                  </td>
                  <td className="muted">{t.notes || "—"}</td>
                  <td className="right mono">{formatCents(t.amount_cents)}</td>
                  <td className="right">
                    <button type="button" className="btn-ghost btn-sm" onClick={() => void remove(t.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
